/**
 * /api/v1/downhole/personnel-rates (api#1057): what each crew role bills a
 * customer per day. Behind the dd_mwd mode, so the read takes `enabled`; read
 * by admin, office_staff and dispatcher, written by admin and office_staff
 * (the tool rate card's roles, lib/downhole-roles.ts).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { unwrapDownhole, unwrapDownholeList } from './useDownhole';
import type { DhPersonnelRate, DhPersonnelRateWrite, DownholeEnvelope } from '../types/downhole';

export interface DhPersonnelRateParams {
  customer_id?: string;
  personnel_role?: string;
  /** 'YYYY-MM-DD': rates in effect on that day. */
  on?: string;
  include_deleted?: 'true';
}

export const personnelRateKeys = {
  all: ['downhole', 'personnel-rates'] as const,
  list: (params?: DhPersonnelRateParams) => ['downhole', 'personnel-rates', params ?? {}] as const,
};

export function useDownholePersonnelRates(params: DhPersonnelRateParams | undefined, enabled: boolean) {
  return useQuery({
    queryKey: personnelRateKeys.list(params),
    queryFn: async () =>
      unwrapDownholeList<DhPersonnelRate>(
        (await apiClient.get<DownholeEnvelope<DhPersonnelRate[]>>('/downhole/personnel-rates', { params })).data,
      ),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function usePersonnelRateWrite<V>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapDownhole<DhPersonnelRate>((await call(vars)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: personnelRateKeys.all }),
  });
}

export function useCreatePersonnelRate() {
  return usePersonnelRateWrite<DhPersonnelRateWrite>((body) =>
    apiClient.post<DownholeEnvelope<DhPersonnelRate>>('/downhole/personnel-rates', body),
  );
}

export function useUpdatePersonnelRate() {
  return usePersonnelRateWrite<{ id: string; body: DhPersonnelRateWrite }>((v) =>
    apiClient.patch<DownholeEnvelope<DhPersonnelRate>>(`/downhole/personnel-rates/${v.id}`, v.body),
  );
}

export function useDeletePersonnelRate() {
  return usePersonnelRateWrite<string>((id) => apiClient.delete<DownholeEnvelope<DhPersonnelRate>>(`/downhole/personnel-rates/${id}`));
}

export function useRestorePersonnelRate() {
  return usePersonnelRateWrite<string>((id) =>
    apiClient.post<DownholeEnvelope<DhPersonnelRate>>(`/downhole/personnel-rates/${id}/restore`),
  );
}
